import {useState, useCallback} from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@sakura-grp/ui/alert-dialog";

interface UseDirtyDialogGuardOptions {
  isDirty: boolean;
  onClose: () => void;
}

/**
 * フォーム入力中のモーダル誤閉じを防ぐフック
 *
 * dirty 状態の時、overlay クリック・Esc・onOpenChange(false) で
 * 確認ダイアログを表示してから閉じる。clean 時はそのまま閉じる。
 *
 * Radix Dialog 向け: dialogProps / contentProps を展開することで overlay/Esc
 * 経由の close を preventDefault + requestClose に差し替える。
 *
 * Vaul Drawer 向け: drag-close は preventDefault できないため、Drawer 側で
 * dismissible={!isDirty} を設定してジェスチャー close を塞ぐ。hook は
 * DirtyConfirmDialog と requestClose のみ使い、やめるボタンから明示的に
 * requestClose を呼び出す構成にする。
 *
 * @example Radix Dialog
 * const { dialogProps, contentProps, DirtyConfirmDialog } = useDirtyDialogGuard({
 *   isDirty: form.formState.isDirty,
 *   onClose: () => setIsOpen(false),
 * });
 *
 * <Dialog open={isOpen} {...dialogProps}>
 *   <DialogContent {...contentProps}>...</DialogContent>
 * </Dialog>
 * <DirtyConfirmDialog />
 *
 * @example Vaul Drawer
 * const { DirtyConfirmDialog, requestClose } = useDirtyDialogGuard({
 *   isDirty: form.formState.isDirty,
 *   onClose: () => setIsOpen(false),
 * });
 *
 * <Drawer open={isOpen} onOpenChange={setIsOpen} dismissible={!form.formState.isDirty}>
 *   <DrawerContent>
 *     ...
 *     <Button onClick={requestClose}>やめる</Button>
 *   </DrawerContent>
 * </Drawer>
 * <DirtyConfirmDialog />
 */
export function useDirtyDialogGuard({isDirty, onClose}: UseDirtyDialogGuardOptions) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const requestClose = useCallback(() => {
    if (isDirty) {
      setConfirmOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleConfirm = useCallback(() => {
    setConfirmOpen(false);
    onClose();
  }, [onClose]);

  const handleCancel = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  // Dialog の onOpenChange を wrap
  const dialogProps = {
    onOpenChange: (open: boolean) => {
      if (!open) requestClose();
    },
  };

  // DialogContent の外クリック / Esc を intercept
  const contentProps = {
    onInteractOutside: (e: Event) => {
      if (isDirty) {
        // ネストされたダイアログ内のクリックは無視（Portal 経由で外側判定されるため）
        const target = (e as PointerEvent).target as HTMLElement | null;
        if (target?.closest('[role="dialog"], [role="alertdialog"]')) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        requestClose();
      }
    },
    onEscapeKeyDown: (e: Event) => {
      if (isDirty) {
        e.preventDefault();
        requestClose();
      }
    },
  };

  function DirtyConfirmDialog() {
    return (
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>入力内容を破棄しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              保存されていない変更があります。このまま閉じると入力内容が失われます。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>戻る</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>破棄する</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return {dialogProps, contentProps, DirtyConfirmDialog, requestClose};
}
